import type {
  InventoryAction,
  InventoryProvider,
  InventoryRepo,
  InventoryTree,
} from '@scaleaq/sitecontrol-lib';

/** Forest assembled from a flat `InventoryRepo[]`, matching the inventory data context. */
export type AssembledInventoryForest = {
  roots: InventoryTree[];
  inventoryById: ReadonlyMap<string, InventoryTree>;
  firstRootInventories: InventoryTree[];
  rootProviders: InventoryProvider[];
  rootActions: InventoryAction[];
};

/**
 * Builds the inventory forest from a flat list. Nodes with no parent, or a parent missing from
 * the list, are roots. Each node's rolled-up `providers` and `actions` are its private sets plus
 * the rolled-up sets of its children. `InventoryRepo` does not carry those rolled-up fields.
 */
export const assembleInventoryForest = (
  nodes: readonly InventoryRepo[] | undefined
): AssembledInventoryForest => {
  if (!nodes?.length) return emptyForest();

  const inventoryById = new Map<string, InventoryTree>();
  for (const node of nodes) {
    if (inventoryById.has(node.id)) continue;
    inventoryById.set(node.id, toTreeNode(node));
  }

  const ordered = [...inventoryById.values()];
  const childIds = new Set<string>();
  for (const node of ordered) {
    const parentId = node.parentId;
    if (!parentId || parentId === node.id) continue;
    const parent = inventoryById.get(parentId);
    if (!parent) continue;
    parent.inventories.push(node);
    childIds.add(node.id);
  }

  const roots = ordered.filter(node => !childIds.has(node.id));
  for (const root of roots) rollup(root, new Set());

  return {
    roots,
    inventoryById,
    firstRootInventories: roots[0]?.inventories ?? [],
    rootProviders: roots.flatMap(root => root.providers),
    rootActions: roots.flatMap(root => root.actions),
  };
};

const emptyForest = (): AssembledInventoryForest => ({
  roots: [],
  inventoryById: new Map(),
  firstRootInventories: [],
  rootProviders: [],
  rootActions: [],
});

const toTreeNode = (node: InventoryRepo): InventoryTree =>
  ({
    ...node,
    inventories: [],
    providers: [...(node.privateProviders ?? [])],
    actions: [...(node.privateActions ?? [])],
  }) as InventoryTree;

const appendUnique = <T>({ target, values }: { target: T[]; values: readonly T[] }) => {
  for (const value of values) {
    if (!target.includes(value)) target.push(value);
  }
};

const rollup = (node: InventoryTree, visiting: Set<string>) => {
  if (visiting.has(node.id)) return;
  visiting.add(node.id);
  for (const child of node.inventories) {
    rollup(child, visiting);
    appendUnique({ target: node.providers, values: child.providers });
    appendUnique({ target: node.actions, values: child.actions });
  }
};
